import { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, Filter, X } from "lucide-react";
import { api } from "@/lib/api";
import T from "@/lib/testIds";

const STOCKS = [
  { v: "", label: "Semua" },
  { v: "ready", label: "Ready" },
  { v: "preorder", label: "Preorder" },
];

export default function FilterSheet({ open, onOpenChange, value, onApply }) {
  const [q, setQ] = useState(value.q || "");
  const [kategori, setKategori] = useState(value.kategori || "Semua");
  const [stockStatus, setStockStatus] = useState(value.stock_status || "");
  const [hargaMax, setHargaMax] = useState(value.harga_max || "");
  const [cats, setCats] = useState([]);

  useEffect(() => {
    if (!open) return;
    api.get("/categories").then((r) => setCats(["Semua", ...(r.data.categories || [])])).catch(() => {});
    setQ(value.q || "");
    setKategori(value.kategori || "Semua");
    setStockStatus(value.stock_status || "");
    setHargaMax(value.harga_max || "");
  }, [open]); // eslint-disable-line

  const apply = () => {
    onApply({
      q: q.trim(),
      kategori,
      stock_status: stockStatus,
      harga_max: hargaMax ? parseInt(hargaMax) : "",
    });
    onOpenChange(false);
  };

  const reset = () => {
    setQ(""); setKategori("Semua"); setStockStatus(""); setHargaMax("");
    onApply({ q: "", kategori: "Semua", stock_status: "", harga_max: "" });
    onOpenChange(false);
  };

  const chip = (active) =>
    active
      ? "bg-sakura-400 text-plum border-plum/10 shadow-[0_4px_14px_rgba(245,169,199,0.4)]"
      : "bg-white/70 dark:bg-plum/30 border-plum/10 text-plum/70 dark:text-cream/70 hover:border-sakura-300";

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        className="rounded-t-[32px] max-h-[88dvh] overflow-y-auto glass border-t border-plum/10 shadow-[0_-20px_60px_rgba(74,44,78,0.15)]"
      >
        <SheetHeader className="text-left">
          <SheetTitle className="font-serif italic text-3xl flex items-center gap-2 text-plum dark:text-cream">
            <Filter className="h-5 w-5 text-sakura-600" strokeWidth={2} />
            Cari & filter
          </SheetTitle>
        </SheetHeader>

        <div className="mt-5 space-y-5 pb-2">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-plum/50 dark:text-cream/50" strokeWidth={2} />
            <Input
              data-testid={T.filterSearchInput}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="cari barang, brand..."
              className="pl-11 h-12 rounded-full border border-plum/15 bg-white/85 dark:bg-plum/20 focus-visible:ring-sakura-400 focus-visible:ring-offset-0"
              onKeyDown={(e) => e.key === "Enter" && apply()}
            />
          </div>

          {/* Kategori */}
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-plum/70 dark:text-cream/70 mb-2.5">
              Kategori
            </div>
            <div className="flex flex-wrap gap-2">
              {cats.map((c) => (
                <button
                  key={c}
                  type="button"
                  data-testid={T.filterCategoryBtn(c)}
                  onClick={() => setKategori(c)}
                  className={`px-3.5 py-1.5 rounded-full text-[11px] font-semibold uppercase tracking-[0.15em] border transition-all active:scale-95 ${chip(kategori === c)}`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>

          {/* Status Stok */}
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-plum/70 dark:text-cream/70 mb-2.5">
              Status stok
            </div>
            <div className="flex flex-wrap gap-2">
              {STOCKS.map((s) => (
                <button
                  key={s.v}
                  type="button"
                  data-testid={T.filterStockBtn(s.v || "all")}
                  onClick={() => setStockStatus(s.v)}
                  className={`px-3.5 py-1.5 rounded-full text-[11px] font-semibold uppercase tracking-[0.15em] border transition-all active:scale-95 ${chip(stockStatus === s.v)}`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          {/* Harga max */}
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-plum/70 dark:text-cream/70 mb-2.5">
              Harga maks (opsional)
            </div>
            <Input
              type="number"
              inputMode="numeric"
              value={hargaMax}
              onChange={(e) => setHargaMax(e.target.value)}
              placeholder="cth: 200000"
              className="h-12 rounded-2xl border border-plum/15 bg-white/85 dark:bg-plum/20"
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-3 pb-[max(1rem,env(safe-area-inset-bottom))] sticky bottom-0 -mx-6 px-6 bg-gradient-to-t from-cream via-cream/95 to-cream/60 dark:from-plum dark:via-plum/95 dark:to-plum/60">
            <Button
              onClick={reset}
              data-testid={T.filterReset}
              variant="ghost"
              className="flex-1 h-12 rounded-full text-[11px] font-semibold uppercase tracking-[0.15em] border border-plum/10 bg-white/70 dark:bg-plum/30 text-plum dark:text-cream hover:bg-white active:scale-95 transition-transform"
            >
              <X className="h-4 w-4 mr-1" strokeWidth={2} /> Reset
            </Button>
            <Button
              onClick={apply}
              data-testid={T.filterApply}
              className="flex-1 h-12 rounded-full text-[11px] font-semibold uppercase tracking-[0.15em] bg-sakura-400 hover:bg-sakura-500 text-plum border border-plum/10 shadow-[0_6px_20px_rgba(245,169,199,0.4)] active:scale-95 transition-transform"
            >
              Terapkan
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
